import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Archive,
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MessageCircle,
  Phone,
  RefreshCw,
  Trash2,
  UserRound,
  X,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import { getAccessToken } from "../../../../awsAuth";
import "./BookingsAll.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

const ACTIVE_STATUSES = [
  "pending",
  "confirmed",
  "reschedule_requested",
  "reschedule_proposed",
];

const ARCHIVE_STATUSES = [
  "completed",
  "cancelled",
  "declined",
];

function pad(value) {
  return String(value).padStart(2, "0");
}

function toDateKey(date) {
  return `${date.getFullYear()}-${pad(
    date.getMonth() + 1
  )}-${pad(date.getDate())}`;
}

function parseDateKey(value) {
  if (!value) return null;

  const date = new Date(`${value}T12:00:00`);

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

function startOfDay(date) {
  const next = new Date(date);

  next.setHours(0, 0, 0, 0);

  return next;
}

function addDays(date, count) {
  const next = new Date(date);

  next.setDate(next.getDate() + count);

  return next;
}

function startOfWeek(date) {
  const next = startOfDay(date);
  const day = next.getDay();

  const offset =
    day === 0
      ? -6
      : 1 - day;

  next.setDate(next.getDate() + offset);

  return next;
}

function bookingStartDate(booking) {
  if (!booking?.date || !booking?.start) {
    return null;
  }

  const [year, month, day] = String(
    booking.date
  )
    .split("-")
    .map(Number);

  const [hours, minutes] = String(
    booking.start
  )
    .split(":")
    .map(Number);

  const date = new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    0,
    0
  );

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

function formatFullDate(dateKey) {
  const date = parseDateKey(dateKey);

  if (!date) return "";

  return date.toLocaleDateString("uk-UA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function formatShortWeekday(date) {
  return date
    .toLocaleDateString("uk-UA", {
      weekday: "short",
    })
    .replace(".", "");
}

function formatMonth(date) {
  return date.toLocaleDateString("uk-UA", {
    month: "long",
    year: "numeric",
  });
}

function statusLabel(status) {
  const labels = {
    pending: "Новий запит",
    confirmed: "Підтверджено",
    declined: "Відхилено",
    cancelled: "Скасовано",
    completed: "Завершено",
    reschedule_requested: "Хоче перенести",
    reschedule_proposed: "Новий час",
  };

  return labels[status] || "Запис";
}

function actionAllowed(status) {
  return [
    "pending",
    "confirmed",
    "reschedule_requested",
    "reschedule_proposed",
  ].includes(status);
}

export default function BookingsAll() {
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [clientProfiles, setClientProfiles] =
  useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [activeTab, setActiveTab] =
    useState("upcoming");

  const [weekStart, setWeekStart] = useState(
    () => startOfWeek(new Date())
  );

  const [selectedDate, setSelectedDate] =
    useState(() => toDateKey(new Date()));

  const [openedBookingId, setOpenedBookingId] =
    useState("");

  const [savingBookingId, setSavingBookingId] =
    useState("");

  const [rescheduleBooking, setRescheduleBooking] =
    useState(null);

  const [rescheduleClosing, setRescheduleClosing] =
    useState(false);

  const [rescheduleDate, setRescheduleDate] =
    useState(() => toDateKey(new Date()));

  const [rescheduleSlots, setRescheduleSlots] =
    useState([]);

  const [rescheduleLoading, setRescheduleLoading] =
    useState(false);

  const weekDays = useMemo(() => {
    return Array.from({
      length: 7,
    }).map((_, index) =>
      addDays(weekStart, index)
    );
  }, [weekStart]);

  const upcomingBookings = useMemo(() => {
    const now = new Date();

    return [...bookings]
      .filter((booking) =>
        ACTIVE_STATUSES.includes(
          booking.status
        )
      )
      .filter((booking) => {
        const date =
          bookingStartDate(booking);

        return date && date >= now;
      })
      .sort((a, b) => {
        const aTime =
          bookingStartDate(a)?.getTime() || 0;

        const bTime =
          bookingStartDate(b)?.getTime() || 0;

        return aTime - bTime;
      });
  }, [bookings]);

  const selectedDayBookings = useMemo(() => {
    return [...bookings]
      .filter((booking) =>
        ACTIVE_STATUSES.includes(
          booking.status
        )
      )
      .filter(
        (booking) =>
          String(booking.date) ===
          String(selectedDate)
      )
      .sort((a, b) =>
        String(a.start || "").localeCompare(
          String(b.start || "")
        )
      );
  }, [bookings, selectedDate]);

  const archivedBookings = useMemo(() => {
    return [...bookings]
      .filter((booking) =>
        ARCHIVE_STATUSES.includes(
          booking.status
        )
      )
      .sort((a, b) => {
        const aTime =
          bookingStartDate(a)?.getTime() || 0;

        const bTime =
          bookingStartDate(b)?.getTime() || 0;

        return bTime - aTime;
      });
  }, [bookings]);

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    if (!rescheduleBooking) return;

    loadRescheduleSlots(rescheduleDate);
  }, [rescheduleBooking, rescheduleDate]);

  async function loadClientProfiles(items) {
  const clientIds = Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((booking) =>
          String(booking?.clientId || "").trim()
        )
        .filter(Boolean)
    )
  );

  if (!clientIds.length) return;

  const missingIds = clientIds.filter(
    (clientId) => !clientProfiles[clientId]
  );

  if (!missingIds.length) return;

  try {
    const token = await getAccessToken().catch(
      () => null
    );

    const results = await Promise.allSettled(
      missingIds.map(async (clientId) => {
        const response = await fetch(
          `${API_BASE}/api/account/${encodeURIComponent(
            clientId
          )}`,
          {
            headers: {
              ...(token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {}),
            },
          }
        );

        const data = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          return null;
        }

        const profile = data?.user || data || {};

        return {
          clientId,
          avatar:
            profile.avatar ||
            profile.photo ||
            profile.profilePhoto ||
            "",
          username:
            profile.username ||
            profile.userName ||
            profile.name ||
            "",
          phone:
            profile.phone ||
            profile.phoneNumber ||
            "",
        };
      })
    );

    const loadedProfiles = {};

    results.forEach((result) => {
      if (
        result.status !== "fulfilled" ||
        !result.value?.clientId
      ) {
        return;
      }

      loadedProfiles[result.value.clientId] =
        result.value;
    });

    if (Object.keys(loadedProfiles).length) {
      setClientProfiles((current) => ({
        ...current,
        ...loadedProfiles,
      }));
    }
  } catch (profilesError) {
    console.error(
      "[BookingsAll] client profiles error:",
      profilesError
    );
  }
}

  async function loadBookings() {
    try {
      setLoading(true);
      setError("");

      const token = await getAccessToken();

      const response = await fetch(
        `${API_BASE}/api/business/booking/requests/me?role=owner`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "Не вдалося завантажити записи"
        );
      }

      const loadedBookings = Array.isArray(
        data?.items
      )
        ? data.items
        : [];

      setBookings(loadedBookings);

      loadClientProfiles(loadedBookings);
    } catch (loadError) {
      console.error(
        "[BookingsAll] load error:",
        loadError
      );

      setError(
        loadError.message ||
          "Не вдалося завантажити записи"
      );
    } finally {
      setLoading(false);
    }
  }

  async function updateBooking(
    bookingId,
    payload,
    successMessage
  ) {
    try {
      setSavingBookingId(bookingId);
      setError("");
      setSuccess("");

      const token = await getAccessToken();

      const response = await fetch(
        `${API_BASE}/api/business/booking/requests/${encodeURIComponent(
          bookingId
        )}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "Не вдалося оновити запис"
        );
      }

      const updated = data?.booking;

      if (updated?.bookingId) {
        setBookings((current) =>
          current.map((booking) =>
            booking.bookingId ===
            updated.bookingId
              ? updated
              : booking
          )
        );
      }

      setSuccess(successMessage);

      window.setTimeout(() => {
        setSuccess("");
      }, 3000);

      return updated || null;
    } catch (actionError) {
      console.error(
        "[BookingsAll] action error:",
        actionError
      );

      setError(
        actionError.message ||
          "Не вдалося оновити запис"
      );

      return null;
    } finally {
      setSavingBookingId("");
    }
  }

  async function deleteArchivedBooking(
    booking
  ) {
    if (!booking?.bookingId) return;

    const accepted = window.confirm(
      "Видалити цей запис з архіву назавжди?"
    );

    if (!accepted) return;

    try {
      setSavingBookingId(
        booking.bookingId
      );

      setError("");
      setSuccess("");

      const token = await getAccessToken();

      const response = await fetch(
        `${API_BASE}/api/business/booking/requests/${encodeURIComponent(
          booking.bookingId
        )}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "Не вдалося видалити запис"
        );
      }

      setBookings((current) =>
        current.filter(
          (item) =>
            item.bookingId !==
            booking.bookingId
        )
      );

      setOpenedBookingId("");

      setSuccess(
        "Запис видалено з архіву"
      );

      window.setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (deleteError) {
      console.error(
        "[BookingsAll] delete error:",
        deleteError
      );

      setError(
        deleteError.message ||
          "Не вдалося видалити запис"
      );
    } finally {
      setSavingBookingId("");
    }
  }

  async function loadRescheduleSlots(
    dateKey
  ) {
    if (
      !rescheduleBooking?.ownerId ||
      !dateKey
    ) {
      return;
    }

    try {
      setRescheduleLoading(true);
      setRescheduleSlots([]);

      const response = await fetch(
        `${API_BASE}/api/business/booking/slots/${encodeURIComponent(
          rescheduleBooking.ownerId
        )}?date=${encodeURIComponent(dateKey)}`
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "Не вдалося завантажити час"
        );
      }

      setRescheduleSlots(
        (
          Array.isArray(data?.slots)
            ? data.slots
            : []
        ).filter((slot) => slot.available)
      );
    } catch (slotError) {
      console.error(
        "[BookingsAll] slots error:",
        slotError
      );

      setRescheduleSlots([]);
    } finally {
      setRescheduleLoading(false);
    }
  }

  function confirmBooking(booking) {
    updateBooking(
      booking.bookingId,
      {
        action: "confirm",
      },
      "Запис підтверджено"
    );
  }

  function declineBooking(booking) {
    const accepted = window.confirm(
      "Відхилити цю заявку?"
    );

    if (!accepted) return;

    updateBooking(
      booking.bookingId,
      {
        action: "decline",
      },
      "Заявку відхилено"
    );
  }

  function cancelBooking(booking) {
    const accepted = window.confirm(
      "Скасувати цей запис?"
    );

    if (!accepted) return;

    updateBooking(
      booking.bookingId,
      {
        action: "owner_cancel",
      },
      "Запис скасовано"
    );
  }

  function openReschedule(booking) {
    setRescheduleClosing(false);
    setRescheduleBooking(booking);

    setRescheduleDate(
      booking.date ||
        toDateKey(new Date())
    );
  }

  function closeReschedule() {
    if (
      !rescheduleBooking ||
      rescheduleClosing
    ) {
      return;
    }

    setRescheduleClosing(true);

    window.setTimeout(() => {
      setRescheduleBooking(null);
      setRescheduleSlots([]);
      setRescheduleClosing(false);
    }, 260);
  }

  async function proposeSlot(slot) {
    if (!rescheduleBooking) return;

    const updated = await updateBooking(
      rescheduleBooking.bookingId,
      {
        action: "propose",
        proposedSlots: [
          {
            date: rescheduleDate,
            start: slot.start,
            end: slot.end,
          },
        ],
      },
      "Новий час запропоновано клієнту"
    );

    if (updated) {
      closeReschedule();
    }
  }

  function openMessages(booking) {
    if (!booking?.clientId) return;

    navigate(
      `/messages?to=${encodeURIComponent(
        booking.clientId
      )}`
    );
  }

  function callClient(booking, phone) {
    const value =
      phone || booking?.clientPhone || "";

    if (!value) return;

    window.location.href = `tel:${value}`;
  }

  function renderBooking(booking) {
    const opened =
      openedBookingId ===
      booking.bookingId;

    const needsAnswer = [
      "pending",
      "reschedule_requested",
    ].includes(booking.status);

    const canEdit = actionAllowed(
      booking.status
    );

    const isArchived =
      ARCHIVE_STATUSES.includes(
        booking.status
      );

    const clientProfile =
      clientProfiles[
        String(booking.clientId || "")
      ] || {};

    const clientAvatar =
      booking.clientAvatar ||
      booking.client?.avatar ||
      clientProfile.avatar ||
      "";

    const clientName =
      clientProfile.username ||
      booking.clientName ||
      "Клієнт";

    const clientPhone =
      clientProfile.phone ||
      booking.clientPhone ||
      "";

    return (
      <article
        key={booking.bookingId}
        className={[
          "baCard",
          opened ? "is-open" : "",
          `is-${
            booking.status || "pending"
          }`,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className="baCard__main"
          aria-expanded={opened}
          onClick={() =>
            setOpenedBookingId((current) =>
              current === booking.bookingId
                ? ""
                : booking.bookingId
            )
          }
        >
          <span className="baCard__avatar">
            {clientAvatar ? (
              <img
                src={clientAvatar}
                alt={
                  booking.clientName ||
                  "Клієнт"
                }
              />
            ) : (
              <UserRound
                size={23}
                strokeWidth={2}
              />
            )}
          </span>

          <span className="baCard__content">
            <strong>
              {booking.clientName ||
                "Клієнт"}
            </strong>

            <em>
              {booking.clientPhone ||
                "Телефон не вказано"}
            </em>
          </span>

          <span
            className={`baStatus is-${
              booking.status || "pending"
            }`}
          >
            {statusLabel(booking.status)}
          </span>

          <ChevronDown
            size={18}
            className="baCard__chevron"
          />
        </button>

        <div
          className={[
            "baCard__detailsWrap",
            opened ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden={!opened}
        >
          <div className="baCard__details">
            <div className="baBookingSummary">
              <span className="baBookingSummary__icon">
                <CalendarDays size={19} />
              </span>

              <span className="baBookingSummary__content">
                <strong>
                  {booking.serviceTitle ||
                    "Онлайн-запис"}
                </strong>

                <em>
                  {formatFullDate(
                    booking.date
                  )}{" "}
                  · {booking.start}–
                  {booking.end}
                </em>
              </span>
            </div>

            {booking.comment && (
              <div className="baComment">
                <MessageCircle size={18} />

                <span>
                  <strong>
                    Коментар клієнта
                  </strong>

                  <em>{booking.comment}</em>
                </span>
              </div>
            )}

            {booking.requestedSlot && (
              <div className="baRequestedSlot">
                <RefreshCw size={18} />

                <span>
                  <strong>
                    Клієнт просить новий час
                  </strong>

                  <em>
                    {formatFullDate(
                      booking.requestedSlot
                        .date
                    )}{" "}
                    ·{" "}
                    {
                      booking.requestedSlot
                        .start
                    }
                    –
                    {
                      booking.requestedSlot
                        .end
                    }
                  </em>
                </span>
              </div>
            )}

            {!isArchived && (
              <div className="baQuickActions">
                <button
                  type="button"
                  disabled={!booking.clientId}
                  onClick={() =>
                    openMessages(booking)
                  }
                >
                  <span className="baQuickActions__circle">
                    <MessageCircle
                      size={20}
                      strokeWidth={2}
                    />
                  </span>

                  <em>Написати</em>
                </button>

                <button
                  type="button"
                  disabled={
                    !booking.clientPhone
                  }
                  onClick={() =>
                    callClient(booking, clientPhone)
                  }
                >
                  <span className="baQuickActions__circle">
                    <Phone
                      size={20}
                      strokeWidth={2}
                    />
                  </span>

                  <em>Подзвонити</em>
                </button>

                {canEdit && (
                  <button
                    type="button"
                    disabled={
                      savingBookingId ===
                      booking.bookingId
                    }
                    onClick={() =>
                      openReschedule(booking)
                    }
                  >
                    <span className="baQuickActions__circle">
                      <RefreshCw
                        size={20}
                        strokeWidth={2}
                      />
                    </span>

                    <em>Перенести</em>
                  </button>
                )}
              </div>
            )}

            {canEdit && (
              <div className="baDecisionActions">
                {needsAnswer && (
                  <button
                    type="button"
                    className="is-confirm"
                    disabled={
                      savingBookingId ===
                      booking.bookingId
                    }
                    onClick={() =>
                      confirmBooking(booking)
                    }
                  >
                    <Check size={18} />

                    {savingBookingId ===
                    booking.bookingId
                      ? "Зберігаємо..."
                      : "Підтвердити"}
                  </button>
                )}

                {booking.status ===
                "pending" ? (
                  <button
                    type="button"
                    className="is-danger"
                    disabled={
                      savingBookingId ===
                      booking.bookingId
                    }
                    onClick={() =>
                      declineBooking(booking)
                    }
                  >
                    <X size={18} />
                    Відхилити
                  </button>
                ) : (
                  <button
                    type="button"
                    className="is-danger"
                    disabled={
                      savingBookingId ===
                      booking.bookingId
                    }
                    onClick={() =>
                      cancelBooking(booking)
                    }
                  >
                    <X size={18} />
                    Скасувати запис
                  </button>
                )}
              </div>
            )}

            {isArchived && (
              <button
                type="button"
                className="baDeleteArchived"
                disabled={
                  savingBookingId ===
                  booking.bookingId
                }
                onClick={() =>
                  deleteArchivedBooking(
                    booking
                  )
                }
              >
                <Trash2 size={18} />

                {savingBookingId ===
                booking.bookingId
                  ? "Видаляємо..."
                  : "Видалити з архіву"}
              </button>
            )}
          </div>
        </div>
      </article>
    );
  }

  if (loading) {
    return (
      <main className="bookingsAll">
        <div className="bookingsAll__state">
          Завантаження записів...
        </div>
      </main>
    );
  }

  return (
    <main className="bookingsAll">
      <header className="bookingsAll__header">
        <button
          type="button"
          className="bookingsAll__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft size={22} />
        </button>

        <div>
          <span>Онлайн-запис</span>

          <h1>Мої записи</h1>

          <p>
            Керуйте записами та розкладом в
            одному місці.
          </p>
        </div>
      </header>

      {error && (
        <div className="bookingsAll__error">
          {error}
        </div>
      )}

      {success && (
        <div className="bookingsAll__success">
          <Check size={18} />
          {success}
        </div>
      )}

      <nav className="bookingsAll__tabs">
        <button
          type="button"
          className={
            activeTab === "upcoming"
              ? "is-active"
              : ""
          }
          onClick={() =>
            setActiveTab("upcoming")
          }
        >
          <Clock3 size={17} />

          Найближчі

          <span>
            {upcomingBookings.length}
          </span>
        </button>

        <button
          type="button"
          className={
            activeTab === "week"
              ? "is-active"
              : ""
          }
          onClick={() =>
            setActiveTab("week")
          }
        >
          <CalendarDays size={17} />
          Тиждень
        </button>

        <button
          type="button"
          className={
            activeTab === "archive"
              ? "is-active"
              : ""
          }
          onClick={() =>
            setActiveTab("archive")
          }
        >
          <Archive size={17} />

          Архів

          <span>
            {archivedBookings.length}
          </span>
        </button>
      </nav>

      {activeTab === "upcoming" && (
        <section
          key="upcoming"
          className="bookingsAll__section"
        >
          <div className="bookingsAll__sectionHead">
            <div>
              <strong>
                Найближчі записи
              </strong>

              <span>
                Від поточного моменту вперед
              </span>
            </div>
          </div>

          {upcomingBookings.length ? (
            <div className="bookingsAll__list">
              {upcomingBookings.map(
                renderBooking
              )}
            </div>
          ) : (
            <div className="bookingsAll__empty">
              <Clock3 size={30} />

              <strong>
                Найближчих записів немає
              </strong>

              <span>
                Нові заявки з’являться тут.
              </span>
            </div>
          )}
        </section>
      )}

      {activeTab === "week" && (
        <section
          key="week"
          className="bookingsAll__section"
        >
          <div className="baWeekHeader">
            <button
              type="button"
              onClick={() =>
                setWeekStart((current) =>
                  addDays(current, -7)
                )
              }
              aria-label="Попередній тиждень"
            >
              <ChevronLeft size={19} />
            </button>

            <strong>
              {formatMonth(weekStart)}
            </strong>

            <button
              type="button"
              onClick={() =>
                setWeekStart((current) =>
                  addDays(current, 7)
                )
              }
              aria-label="Наступний тиждень"
            >
              <ChevronRight size={19} />
            </button>
          </div>

          <div
            key={toDateKey(weekStart)}
            className="baWeekDays"
          >
            {weekDays.map((date) => {
              const dateKey =
                toDateKey(date);

              const active =
                dateKey === selectedDate;

              const count = bookings.filter(
                (booking) =>
                  booking.date === dateKey &&
                  ACTIVE_STATUSES.includes(
                    booking.status
                  )
              ).length;

              return (
                <button
                  key={dateKey}
                  type="button"
                  className={
                    active
                      ? "is-active"
                      : ""
                  }
                  onClick={() =>
                    setSelectedDate(dateKey)
                  }
                >
                  <span>
                    {formatShortWeekday(
                      date
                    )}
                  </span>

                  <strong>
                    {date.getDate()}
                  </strong>

                  {count > 0 && (
                    <em>{count}</em>
                  )}
                </button>
              );
            })}
          </div>

          <div className="bookingsAll__sectionHead">
            <div>
              <strong>
                {formatFullDate(
                  selectedDate
                )}
              </strong>

              <span>
                {selectedDayBookings.length}{" "}
                записів
              </span>
            </div>
          </div>

          {selectedDayBookings.length ? (
            <div
              key={selectedDate}
              className="bookingsAll__list"
            >
              {selectedDayBookings.map(
                renderBooking
              )}
            </div>
          ) : (
            <div
              key={`empty-${selectedDate}`}
              className="bookingsAll__empty"
            >
              <CalendarDays size={30} />

              <strong>
                На цей день записів немає
              </strong>
            </div>
          )}
        </section>
      )}

      {activeTab === "archive" && (
        <section
          key="archive"
          className="bookingsAll__section"
        >
          <div className="bookingsAll__sectionHead">
            <div>
              <strong>Архів записів</strong>

              <span>
                Скасовані, відхилені та
                завершені
              </span>
            </div>
          </div>

          {archivedBookings.length ? (
            <div className="bookingsAll__list">
              {archivedBookings.map(
                renderBooking
              )}
            </div>
          ) : (
            <div className="bookingsAll__empty">
              <Archive size={30} />

              <strong>
                Архів порожній
              </strong>
            </div>
          )}
        </section>
      )}

      {rescheduleBooking && (
        <div
          className={[
            "baModal",
            rescheduleClosing
              ? "is-closing"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="dialog"
          aria-modal="true"
          aria-label="Перенесення запису"
        >
          <button
            type="button"
            className="baModal__backdrop"
            onClick={closeReschedule}
            aria-label="Закрити"
          />

          <section className="baModal__panel">
            <div className="baModal__head">
              <div>
                <span>
                  Перенесення запису
                </span>

                <strong>
                  {rescheduleBooking.serviceTitle ||
                    "Онлайн-запис"}
                </strong>
              </div>

              <button
                type="button"
                onClick={closeReschedule}
                aria-label="Закрити"
              >
                <X size={20} />
              </button>
            </div>

            <label className="baModal__date">
              <span>Нова дата</span>

              <input
                type="date"
                value={rescheduleDate}
                min={toDateKey(new Date())}
                onChange={(event) =>
                  setRescheduleDate(
                    event.target.value
                  )
                }
              />
            </label>

            <div className="baModal__slots">
              {rescheduleLoading ? (
                <div className="baModal__state">
                  Завантаження часу...
                </div>
              ) : rescheduleSlots.length ? (
                rescheduleSlots.map(
                  (slot) => (
                    <button
                      key={`${rescheduleDate}-${slot.start}`}
                      type="button"
                      disabled={
                        savingBookingId ===
                          rescheduleBooking.bookingId ||
                        rescheduleClosing
                      }
                      onClick={() =>
                        proposeSlot(slot)
                      }
                    >
                      <strong>
                        {slot.start}
                      </strong>

                      <span>
                        до {slot.end}
                      </span>
                    </button>
                  )
                )
              ) : (
                <div className="baModal__state">
                  На цю дату немає вільного
                  часу.
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}